import React from 'react';
import { 
  BookOpen, 
  Users, 
  DollarSign, 
  FileText, 
  Wrench,
  CreditCard,
  Clock,
  Home,
  CheckCircle,
  ArrowRight,
  MessageCircle,
  Mail,
  Phone,
  Shield,
  MapPin,
  TrendingUp,
  BarChart3,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { mockData } from '../data/mock';

export const PricingSection = ({ scrollToSection }) => (
  <section id="membership" className="py-20 bg-gradient-to-b from-slate-800 to-slate-900">
    <div className="container mx-auto px-4">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <h2 className="text-4xl font-bold text-white mb-6 font-serif">
          Choose Your Membership Level
        </h2>
        <p className="text-xl text-amber-200 leading-relaxed">
          Private membership benefits designed to support property owners while preserving your rights and autonomy.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {/* Basic - Free */}
        <Card className="relative bg-white border-2 border-green-200 hover:border-green-400 transition-all duration-300">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-slate-800">Basic</CardTitle>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-green-600 mb-2">FREE</div>
              <p className="text-slate-600">Educational Resources</p>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3 mb-8">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-slate-700">Legal documents & forms access</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-slate-700">Educational resource library</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-slate-700">⭐ FREE GC & Contractor sign-up</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-slate-700">Member community access</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-slate-700">Monthly educational webinars</span>
              </div>
            </div>
            <Button onClick={() => scrollToSection('contact')} className="w-full bg-green-600 hover:bg-green-700">
              Get Started Free
            </Button>
          </CardContent>
        </Card>

        {/* Standard - $50 */}
        <Card className="relative bg-white border-2 border-amber-200 hover:border-amber-400 transition-all duration-300 scale-105">
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <Badge className="bg-amber-500 text-white px-4 py-1">POPULAR</Badge>
          </div>
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl text-slate-800">Standard</CardTitle>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-amber-600 mb-2">$50</div>
              <p className="text-slate-600">per month</p>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3 mb-8">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">Everything in Basic, plus:</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">Member Portal access</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">Property tracking & management</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">Tenant payment coordination</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">Maintenance request system</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <span className="text-slate-700">🔧 Access to General Contractor & Skilled Laborers Network</span>
              </div>
            </div>
            <Button onClick={() => scrollToSection('contact')} className="w-full bg-amber-600 hover:bg-amber-700">
              Choose Standard
            </Button>
          </CardContent>
        </Card>

        {/* Premium - $100 */}
        <Card className="relative bg-white/10 backdrop-blur-sm border-2 border-amber-400/30 hover:border-amber-300 transition-all duration-300 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-200 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-blue-900" />
            </div>
            <CardTitle className="text-2xl font-bold text-white mb-2 font-serif">Premium Membership</CardTitle>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500 mb-2">$100</div>
              <p className="text-amber-200 mb-6">per month - Full Access</p>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3 mb-8">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-white">Everything in Standard, plus:</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Premium member support</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Advanced reporting tools</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Accounting software integration feature</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Land Trust Education and Support</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Priority access to new features</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                <span className="text-amber-100">Exclusive member events</span>
              </div>
            </div>
            <Button onClick={() => scrollToSection('contact')} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-semibold">
              Choose Premium
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="text-center mt-12">
        <p className="text-slate-600 mb-4">
          Administrative coordination and educational resources exclusively for private members
        </p>
        <p>* Private Member Association operating under private contract law</p>
      </div>
    </div>
  </section>
);

export const TargetAudienceSection = () => (
  <section className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-slate-800 mb-6">Who We Serve</h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Private Member Association for property owners, investors, and managers seeking administrative coordination and privacy protection.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {mockData.targetAudiences.map((audience, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow text-center">
            <CardContent className="p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-200 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <audience.icon className="w-8 h-8 text-blue-900" />
              </div>
              <h3 className="text-lg font-semibold mb-3">{audience.name}</h3>
              <p className="text-slate-600 text-sm">{audience.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

export const TestimonialsSection = () => (
  <section id="testimonials" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-slate-800 mb-6 font-serif">Member Success Stories</h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Real experiences from property owners who've benefited from our private member association.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {mockData.testimonials.map((testimonial, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-900 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">{testimonial.name}</h4>
                  <p className="text-sm text-slate-600">{testimonial.role}</p>
                </div>
              </div>
              <p className="text-slate-700 mb-4">"{testimonial.content}"</p>
              <div className="flex text-yellow-400">
                {'★'.repeat(5)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

export const FAQSection = ({ scrollToSection }) => (
  <section id="faq" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-slate-800 mb-6">Frequently Asked Questions</h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Common questions about our Private Member Association and services.
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {mockData.faqs.map((faq, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">{faq.question}</h3>
              <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center mt-12">
        <p className="text-slate-600 mb-4">Still have questions?</p>
        <Button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 border border-amber-400/30">
          Contact Us
        </Button>
      </div>
    </div>
  </section>
);

export const ComparisonSection = () => (
  <section id="comparison" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-slate-800 mb-6">
          LPA vs Traditional Property Management in Chicago
        </h2>
        <p className="text-xl text-slate-600 max-w-4xl mx-auto">
          Compare Legacy Property Association's flat-rate membership model with traditional Chicago property management companies charging 8-12% of rent plus additional fees.
        </p>
      </div>

      {/* Pricing Comparison */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-red-800">Traditional Property Management</CardTitle>
            <p className="text-red-600">Percentage-based + Multiple Fees</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Management Fee:</span>
                <span className="font-semibold text-red-700">8-12% of rent</span>
              </div>
              <div className="flex justify-between">
                <span>Leasing Fee:</span>
                <span className="font-semibold text-red-700">$300-800</span>
              </div>
              <div className="flex justify-between">
                <span>Renewal Fee:</span>
                <span className="font-semibold text-red-700">$150-400</span>
              </div>
              <div className="flex justify-between">
                <span>Setup Fee:</span>
                <span className="font-semibold text-red-700">$200-500</span>
              </div>
              <div className="flex justify-between">
                <span>Inspection Fee:</span>
                <span className="font-semibold text-red-700">$75-150 each</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-blue-800">Legacy Property Association</CardTitle>
            <p className="text-blue-600">Flat-Rate Membership Model</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Standard Membership:</span>
                <span className="font-semibold text-blue-700">$50/month</span>
              </div>
              <div className="flex justify-between">
                <span>Premium Membership:</span>
                <span className="font-semibold text-blue-700">$100/month</span>
              </div>
              <div className="flex justify-between">
                <span>Leasing Support:</span>
                <span className="font-semibold text-green-600">Included</span>
              </div>
              <div className="flex justify-between">
                <span>Renewal Support:</span>
                <span className="font-semibold text-green-600">Included</span>
              </div>
              <div className="flex justify-between">
                <span>Setup & Onboarding:</span>
                <span className="font-semibold text-green-600">Free</span>
              </div>
              <div className="flex justify-between">
                <span>Digital Tools & Training:</span>
                <span className="font-semibold text-green-600">Included</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Break-Even Analysis */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-slate-800 mb-4">Monthly Cost Comparison by Rent Amount</h3>
          <p className="text-slate-600">See where LPA becomes more cost-effective than traditional management</p>
        </div>
        
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left">Monthly Rent</th>
                    <th className="px-4 py-3 text-center text-red-700">Traditional (8%)</th>
                    <th className="px-4 py-3 text-center text-red-800">Traditional (12%)</th>
                    <th className="px-4 py-3 text-center text-blue-700">LPA Standard</th>
                    <th className="px-4 py-3 text-center text-blue-800">LPA Premium</th>
                    <th className="px-4 py-3 text-center">LPA Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {mockData.comparison.breakEvenPoints.map((point, index) => {
                    const savingsVs8 = point.traditional8 - point.lpaPremium;
                    const savingsVs12 = point.traditional12 - point.lpaPremium;
                    const maxSavings = Math.max(savingsVs8, savingsVs12);
                    
                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-4 py-3 font-semibold">${point.rentAmount}</td>
                        <td className="px-4 py-3 text-center text-red-700">${point.traditional8}</td>
                        <td className="px-4 py-3 text-center text-red-800">${point.traditional12}</td>
                        <td className="px-4 py-3 text-center text-blue-700">${point.lpaStandard}</td>
                        <td className="px-4 py-3 text-center text-blue-800">${point.lpaPremium}</td>
                        <td className="px-4 py-3 text-center">
                          {maxSavings > 0 ? (
                            <span className="text-green-600 font-semibold">+${maxSavings}</span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Comparison */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-slate-800 mb-4">Feature-by-Feature Comparison</h3>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left">Feature</th>
                    <th className="px-4 py-3 text-center">Traditional Management</th>
                    <th className="px-4 py-3 text-center">Legacy Property Association</th>
                    <th className="px-4 py-3 text-center">LPA Advantage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-4 font-semibold">Rent Collection</td>
                    <td className="px-4 py-4 text-center text-slate-600">Manual collection, 3-5 day delays</td>
                    <td className="px-4 py-4 text-center text-blue-600">Automated tracking, real-time updates</td>
                    <td className="px-4 py-4 text-center">
                      <Badge className="bg-green-100 text-green-800">Automation</Badge>
                    </td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="px-4 py-4 font-semibold">Maintenance Coordination</td>
                    <td className="px-4 py-4 text-center text-slate-600">Phone/email, markup on repairs</td>
                    <td className="px-4 py-4 text-center text-blue-600">Digital platform, vetted contractors</td>
                    <td className="px-4 py-4 text-center">
                      <Badge className="bg-green-100 text-green-800">Cost Control</Badge>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-4 font-semibold">Lease Tracking</td>
                    <td className="px-4 py-4 text-center text-slate-600">Basic tracking, limited access</td>
                    <td className="px-4 py-4 text-center text-blue-600">Full digital access, document storage</td>
                    <td className="px-4 py-4 text-center">
                      <Badge className="bg-green-100 text-green-800">Transparency</Badge>
                    </td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="px-4 py-4 font-semibold">Tenant Relationship</td>
                    <td className="px-4 py-4 text-center text-slate-600">Loss of direct contact</td>
                    <td className="px-4 py-4 text-center text-blue-600">Maintain direct relationships</td>
                    <td className="px-4 py-4 text-center">
                      <Badge className="bg-green-100 text-green-800">Control</Badge>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-4 font-semibold">Financial Transparency</td>
                    <td className="px-4 py-4 text-center text-slate-600">Limited reporting</td>
                    <td className="px-4 py-4 text-center text-blue-600">Full financial transparency</td>
                    <td className="px-4 py-4 text-center">
                      <Badge className="bg-green-100 text-green-800">Visibility</Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

export const FooterSection = ({ scrollToSection }) => (
  <footer className="bg-slate-900 text-white py-16">
    <div className="container mx-auto px-4">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        
        <div>
          <h4 className="font-semibold text-white mb-4 font-serif">Legacy Property Association</h4>
          <p className="text-slate-400 mb-4">
            Private Member Association for property owners and investors seeking administrative coordination and educational resources.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 font-serif">Member Services</h4>
          <div className="space-y-2 text-slate-400">
            <button 
              onClick={() => {
                // Check if user has Basic membership stored
                const basicMember = localStorage.getItem('basicMemberApplication');
                if (basicMember) {
                  window.location.href = '/basic-portal';
                } else {
                  // Scroll to membership section and highlight Basic membership
                  scrollToSection('membership');
                  setTimeout(() => {
                    alert('Apply for FREE Basic Membership to access Member Resources!\n\nBasic membership includes:\n• Educational resources library\n• Legal documents & forms\n• Property management templates\n• Member community access');
                  }, 500);
                }
              }} 
              className="block hover:text-white transition-colors text-left"
            >
              Basic Member Resources
            </button>
            <button 
              onClick={() => scrollToSection('membership')} 
              className="block hover:text-white transition-colors text-left"
            >
              Membership Plans
            </button>
            <button 
              onClick={() => window.location.href = '/admin'} 
              className="block hover:text-white transition-colors text-left"
            >
              Administrative Coordination
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className="block hover:text-white transition-colors text-left"
            >
              Our Services
            </button>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 font-serif">Portal Access</h4>
          <div className="space-y-2 text-slate-400">
            <button
              onClick={() => {
                // Only show paid portals to verified paid members  
                const basicMember = JSON.parse(localStorage.getItem('basicMemberApplication') || '{}');
                const legacyApp = JSON.parse(localStorage.getItem('legacyPropertyApplication') || '{}');
                
                const membershipLevel = basicMember.membershipLevel || legacyApp.membershipLevel || 'Basic';
                const isPaidMember = membershipLevel === 'Standard' || membershipLevel === 'Premium';
                const hasActiveSubscription = basicMember.subscription_id || legacyApp.subscription_id;
                
                if (isPaidMember && hasActiveSubscription) {
                  // Verified paid member - allow access to owner portal
                  const portalUrl = process.env.REACT_APP_PROPTECH_LOGIN_URL;
                  window.open(portalUrl, '_blank');
                } else {
                  // Redirect to membership upgrade
                  alert('🔒 Premium Portal Access Required\n\nOwner Portal requires Standard ($50/month) or Premium ($100/month) membership.\n\nPlease upgrade your membership to access advanced property management features.');
                  window.location.href = '#membership';
                }
              }}
              className="block hover:text-white transition-colors text-left"
            >
              Owner/Investor Portal 🔒
            </button>
            <button
              onClick={() => {
                // Only show paid portals to verified paid members
                const basicMember = JSON.parse(localStorage.getItem('basicMemberApplication') || '{}');
                const legacyApp = JSON.parse(localStorage.getItem('legacyPropertyApplication') || '{}');
                
                const membershipLevel = basicMember.membershipLevel || legacyApp.membershipLevel || 'Basic';
                const isPaidMember = membershipLevel === 'Standard' || membershipLevel === 'Premium';
                const hasActiveSubscription = basicMember.subscription_id || legacyApp.subscription_id;
                
                if (isPaidMember && hasActiveSubscription) {
                  // Verified paid member - allow access to tenant portal
                  const portalUrl = process.env.REACT_APP_PROPTECH_TENANT_URL;
                  window.open(portalUrl, '_blank');
                } else {
                  // Redirect to membership upgrade
                  alert('🔒 Premium Portal Access Required\n\nTenant Portal requires Standard ($50/month) or Premium ($100/month) membership.\n\nPlease upgrade your membership to access tenant management features.');
                  window.location.href = '#membership';
                }
              }}
              className="block hover:text-white transition-colors text-left"
            >
              Tenant Portal 🔒
            </button>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 font-serif">Information</h4>
          <div className="space-y-2 text-slate-400 mb-6">
            <button onClick={() => window.location.href = '/member-success-stories'} className="block hover:text-amber-300 transition-colors text-left">
              Member Success Stories
            </button>
            <button onClick={() => window.location.href = '/faq'} className="block hover:text-white transition-colors text-left">
              Frequently Asked Questions
            </button>
            <button onClick={() => scrollToSection('contact')} className="block hover:text-white transition-colors text-left">
              Contact Us
            </button>
          </div>
          
          <h5 className="font-medium text-white mb-3 text-sm">Contact Information</h5>
          <div className="space-y-2 text-sm text-slate-400">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>info@lpassociation.org</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4" />
              <span>(312) 899-6169</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>Illinois, USA</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-t border-slate-700 pt-8">
        <div className="flex flex-col sm:flex-row justify-between items-center text-slate-400">
          <p>&copy; 2025 Legacy Property Association. All rights reserved. Private Member Association in Illinois.</p>
          <div className="mt-2 sm:mt-0">
            <a 
              href="/admin" 
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
              title="Admin Access"
            >
              Admin
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
);